import {
  ExternalRootFragment,
  FanPostFragment,
  NoteFragment,
  PollFragment,
  QuickPostFragment,
  UserAdminQueryFragment,
  UserMessageFragment,
} from "@framework/cyclone/graphql/query";
import {
  AnyContentObject,
  Note,
  Poll,
  QuickPost,
  SeenState,
  User,
  UserMessage,
  UserSettings,
} from "@framework/cyclone/types";

export interface DeleteContentObjectMutationResponse {
  deleteContent?: {
    id: string;
    deletedAt: string | null;
  };
}

export const DeleteContentObjectMutation = `mutation DeleteContentMutation($communityID: String!, $contentID: String!) {
    deleteContent(input: { id: $contentID, communityID: $communityID }) {
        id
        deletedAt
    }
  }`;

export interface PinContentObjectMutationResponse {
  pinContent?: AnyContentObject | null;
}

export const PinContentObjectMutation = `mutation PinContentMutation($communityID: String!, $objectID: String!) {
    pinContent(input: { objectID: $objectID, communityID: $communityID }) {
        id
        ... on FanPost {
            pinnedAt
        }
    }
  }`;

export interface UnpinContentObjectMutationResponse {
  removePinnedContent?: AnyContentObject | null;
}

export const UnpinContentObjectMutation = `mutation UnpinContentMutation($communityID: String!) {
    removePinnedContent(input: { communityID: $communityID }) {
      id
    }
  }`;

export interface RejectContentMutationResponse {
  rejectContent?: AnyContentObject | null;
}

export const RejectContentMutation = `
  mutation RejectContentMutation($communityID: String!, $contentID: String!) {
    rejectContent(input: { communityID: $communityID, contentID: $contentID }) {
      ${QuickPostFragment}
      ... on FanPost {
        ${FanPostFragment}
      }
    }
  }
`;

export const CreateSSOUserMutation = `
  mutation CreateSSOUserMutation($userID: String!, $email: String, $commentingName: String!, $role: Role, $avatarUrl: String, $profileId: String, $profileUrl: String) {
    createSSOUser(input: { userID: $userID, email: $email, commentingName: $commentingName, role: $role, avatarUrl: $avatarUrl, profileId: $profileId, profileUrl: $profileUrl }) {
      ${UserAdminQueryFragment}
    }
  }
`;

export const BanUserMutation = `
  mutation BanUserMutation($userID: String!, $description: String, $communityIDs: [String!]) {
    banUser(input: { userID: $userID, description: $description, communityIDs: $communityIDs }) {
      ${UserAdminQueryFragment}
    }
  }
`;

export const RemoveUserBanMutation = `
  mutation RemoveUserBanMutation($userID: String!, $communityIDs: [String!]) {
    removeUserBan(input: { userID: $userID, communityIDs: $communityIDs }) {
      ${UserAdminQueryFragment}
    }
  }
`;

export const SuspendUserMutation = `
  mutation SuspendUserMutation($userID: String!, $description: String, $expiresAt: DateTime) {
    suspendUser(input: { userID: $userID, description: $description, expiresAt: $expiresAt }) {
      ${UserAdminQueryFragment}
    }
  }
`;

export const RemoveUserSuspensionMutation = `
  mutation RemoveUserSuspensionMutation($userID: String!) {
    removeUserSuspension(input: { userID: $userID }) {
      ${UserAdminQueryFragment}
    }
  }
`;

export const UpdateUserRoleMutation = `
  mutation UpdateUserRoleMutation($userID: String!, $role: Role!) {
    updateUserRole(input: { userID: $userID, role: $role }) {
      ${UserAdminQueryFragment}
    }
  }
`;

export const AddTrustedUserMutation = `
  mutation AddTrustedUserMutation($userID: String!, $communityID: String!) {
    addTrustedUser(input: { userID: $userID, communityID: $communityID }) {
      id
      ssoID
      email
      communityID
    }
  }
`;

export const RemoveTrustedUserMutation = `
  mutation RemoveTrustedUserMutation($userID: String!, $communityID: String!) {
    removeTrustedUser(input: { userID: $userID, communityID: $communityID }) {
      id
    }
  }
`;

export interface TrustedUser {
  id: string;
  ssoID: string;
  email: string;
  communityID: string;
}

export interface CloseRepliesMutationResponse {
  closeReplies?: AnyContentObject | null;
}

export const CloseRepliesMutation = `
  mutation CloseRepliesMutation($communityID: String!, $contentID: String!) {
    closeReplies(communityID: $communityID, contentID: $contentID) {
      ${QuickPostFragment}
      ... on FanPost {
        ${FanPostFragment}
      }
      ... on ExternalRoot {
        ${ExternalRootFragment}
      }
    }
  }
`;

export interface OpenRepliesMutationResponse {
  openReplies?: AnyContentObject | null;
}

export const OpenRepliesMutation = `
  mutation OpenRepliesMutation($communityID: String!, $contentID: String!) {
    openReplies(communityID: $communityID, contentID: $contentID) {
      ${QuickPostFragment}
      ... on FanPost {
        ${FanPostFragment}
      }
      ... on ExternalRoot {
        ${ExternalRootFragment}
      }
    }
  }
`;

export interface SetContentPremodMutationResponse {
  setContentPremod?: AnyContentObject | null;
}

export const SetContentPremodMutation = `
  mutation SetContentPremodMutation(
    $communityID: String!
    $contentID: String!
    $premod: Boolean!
  ) {
    setContentPremod(
      input: {
        communityID: $communityID
        contentID: $contentID
        premod: $premod
      }
    ) {
      ${QuickPostFragment}
      ... on FanPost {
        ${FanPostFragment}
        premod
      }
      ... on ExternalRoot {
        ${ExternalRootFragment}
        premod
      }
    }
  }
`;

export const createQuickPostMutation = `
mutation CreateQuickPost( $communityID: String!,
  $parentID: String,
  $body: String!,
  $opener: String,
  $attachments: [AttachmentInput!],
  $federate: Boolean,
  $venueName: String,
  $venueCity: String,
  $venueState: String) {
    createQuickPostContent(input: {
      communityID: $communityID,
      parentID: $parentID,
      body: $body,
      opener: $opener,
      attachments: $attachments,
      federate: $federate,
      venueName: $venueName,
      venueCity: $venueCity,
      venueState: $venueState
    }) {
      ${QuickPostFragment}
    }
  }
`;

export interface UpdateUserSettingsMutationResponse {
  updateUserSettings: UserSettings;
}

export const updateUserSettingsMutation = `
  mutation UpdateUserSettings($userID: String!, $settings: UserSettingsInput!, $communityID: String!) {
    updateUserSettings(input: {
      userID: $userID,
      settings: $settings
      communityID: $communityID
    }) {
      fanPostFeedMode
      replySort
      unfurlMediaEmbeds
    }
  }
`;

export interface AcknowledgeUserMessageMutationResponse {
  acknowledgeUserMessage?: UserMessage | null;
}

export const acknowledgeUserMessageMutation = `
  mutation AcknowledgeUserMessage($id: String!, $communityID: String!) {
    acknowledgeUserMessage(input: { id: $id, communityID: $communityID }) {
      ${UserMessageFragment}
    }
  }
`;

export interface CreateQuickPostResponse {
  createQuickPostContent?: QuickPost;
}

export const editQuickPostMutation = `
mutation EditQuickPost( $communityID: String!, $contentID: String!, $body: String!, $attachments: [AttachmentInput!]) {
  editPost(input: {
    communityID: $communityID,
    contentID: $contentID,
    body: $body,
    attachments: $attachments,
  }) {
    ${QuickPostFragment}
  }
}
`;

export interface EditQuickPostResponse {
  editPost?: QuickPost;
}

export interface EditQuickPostContentMutationResponse {
  editQuickPostContent?: QuickPost;
}

export const editQuickPostContentMutation = `
mutation EditQuickPostContent($id: String!, $communityID: String!, $body: String!, $opener: String, $attachments: [AttachmentInput!]) {
  editQuickPostContent(input: {
    id: $id
    communityID: $communityID
    body: $body
    opener: $opener
    attachments: $attachments
  }) {
    ${QuickPostFragment}
  }
}
`;

export interface RecContentMutationResponse {
  recContent: AnyContentObject;
}

export const recContentMutation = `
  mutation RecFanPost($communityID: String!, $objectID: String!) {
    recContent(input: {
      communityID: $communityID,
      objectID: $objectID
    }) {
      id
      ${QuickPostFragment}
      ... on FanPost {
        ${FanPostFragment}
      }
      ... on Note {
        venueID
        isPrivate
        isEdited
        isRecced
        isReported
        recs
        replyCount
        directReplyCount
      }
    }
  }
`;

export interface UnrecContentMutationResponse {
  unrecContent: AnyContentObject;
}

export const unrecContentMutation = `
  mutation RecFanPost($communityID: String!, $objectID: String!) {
    unrecContent(input: {
      communityID: $communityID,
      objectID: $objectID
    }) {
      id
      ${QuickPostFragment}
      ... on FanPost {
        ${FanPostFragment}
      }
      ... on Note {
        venueID
        isPrivate
        isEdited
        isRecced
        isReported
        recs
        replyCount
        directReplyCount
      }
    }
  }
`;

export interface ReportContentMutationResponse {
  reportContent: AnyContentObject | null;
}

export const reportContentMutation = `
  mutation ReportContentMutation(
    $communityID: String!,
    $contentID: String!,
    $reason: ReportedContentReason!,
    $description: String
  ) {
    reportContent(input: {
      communityID: $communityID,
      contentID: $contentID,
      reason: $reason,
      description: $description
    }) {
      id
      ${QuickPostFragment}
      ... on FanPost {
        ${FanPostFragment}
      }
    }
  }
`;

export type MarkSeenMutationResponse = {
  markSeen: SeenState[];
};

export const markSeenMutation = `
  mutation MarkSeenMutation(
    $communityID: String!,
    $rootID: String!,
    $contentIDs: [String!]!
  ) {
    markSeen(input: {
      communityID: $communityID,
      rootID: $rootID,
      contentIDs: $contentIDs,
    }) {
      communityID
      contentID
      seen
    }
  }
`;

export interface UpdateAttachmentDimensionsMutationResponse {
  updateAttachmentDimensions: AnyContentObject;
}

export const updateAttachmentDimensionsMutation = `
  mutation UpdateAttachmentDimensions($communityID: String!, $id: String!, $heightToWidthRatio: Float!, $attachmentID: String!) {
    updateAttachmentDimensions(input: { communityID: $communityID, id: $id, heightToWidthRatio: $heightToWidthRatio, attachmentID: $attachmentID }) {
      id
    }
  }
`;

export interface UpdateUserAvatarByEmailMutationResponse {
  updateUserAvatar: Pick<User, "id" | "email" | "avatar">;
}

export const updateUserAvatarByEmailMutation = /* GraphQL */ `
  mutation UpdateUserAvatarByEmail(
    $communityID: String!
    $email: String!
    $avatar: String
  ) {
    updateUserAvatarByEmail(
      input: { communityID: $communityID, email: $email, avatar: $avatar }
    ) {
      id
      email
      avatar
    }
  }
`;

export interface SyncUserProfileByEmailMutationResponse {
  syncUserProfileByEmail: Pick<User, "id" | "email">;
}

export const syncUserProfileByEmailMutation = /* GraphQL */ `
  mutation SyncUserProfileByEmail(
    $communityID: String!
    $email: String!
    $profileID: String
    $profileURL: String
    $displayName: String
  ) {
    syncUserProfileByEmail(
      input: {
        communityID: $communityID
        email: $email
        profileID: $profileID
        profileURL: $profileURL
        displayName: $displayName
      }
    ) {
      id
      email
    }
  }
`;

export interface IgnoreUserMutationResponse {
  ignoreUser: {
    id: string;
    user: Pick<User, "id" | "username">;
  };
}

export const ignoreUserMutation = /* GraphQL */ `
  mutation IgnoreUser($userID: String!, $communityID: String!) {
    ignoreUser(userID: $userID, communityID: $communityID) {
      id
      user {
        id
        username
      }
    }
  }
`;

export interface UnignoreUserMutationResponse {
  unignoreUser: Pick<User, "id">;
}

export const unignoreUserMutation = /* GraphQL */ `
  mutation UnignoreUser($userID: String!, $communityID: String!) {
    unignoreUser(userID: $userID, communityID: $communityID) {
      id
    }
  }
`;

export interface CreateNoteMutationResponse {
  createNote?: Note;
}

export const createNoteMutation = `
  mutation CreateNote(
    $communityID: String!,
    $venueID: String!,
    $body: String!,
    $attachments: [AttachmentInput!],
    $isPrivate: Boolean,
    $venueName: String,
    $venueCity: String,
    $venueState: String
  ) {
    createNote(input: {
      communityID: $communityID,
      venueID: $venueID,
      body: $body,
      attachments: $attachments,
      isPrivate: $isPrivate,
      venueName: $venueName,
      venueCity: $venueCity,
      venueState: $venueState
    }) {
      ... on Note {
        ${NoteFragment}
      }
    }
  }
`;

export interface DeleteNoteMutationResponse {
  deleteNote?: Note;
}

export const deleteNoteMutation = `
  mutation DeleteNote($communityID: String!, $noteId: String!) {
    deleteNote(communityID: $communityID, noteId: $noteId) {
      ... on Note {
        id
        deletedAt
      }
    }
  }
`;

export interface EditNoteMutationResponse {
  editPost?: Note;
}

export const editNoteMutation = `
  mutation EditNote(
    $communityID: String!,
    $contentID: String!,
    $body: String!,
    $attachments: [AttachmentInput!],
    $isPrivate: Boolean
  ) {
    editPost(input: {
      communityID: $communityID,
      contentID: $contentID,
      body: $body,
      attachments: $attachments,
      isPrivate: $isPrivate
    }) {
      ... on Note {
        ${NoteFragment}
      }
    }
  }
`;

export interface CastPollVoteMutationResponse {
  castPollVote?: Poll;
}

export const castPollVoteMutation = `
  mutation CastPollVote($communityID: String!, $pollID: String!, $optionID: String!) {
    castPollVote(communityID: $communityID, pollID: $pollID, optionID: $optionID) {
      ${PollFragment}
    }
  }
`;

export interface FanPostTrustedStatus {
  id: string;
  authorIsStaff?: boolean | null;
  isReccedByStaff?: boolean | null;
  isTrusted?: boolean | null;
}

export interface SetContentIsTrustedMutationResponse {
  setContentIsTrusted?: FanPostTrustedStatus | null;
}

export const SetContentIsTrustedMutation = `
  mutation SetContentIsTrustedMutation(
    $communityID: String!
    $contentID: String!
    $isTrusted: Boolean!
  ) {
    setContentIsTrusted(
      input: {
        communityID: $communityID
        contentID: $contentID
        isTrusted: $isTrusted
      }
    ) {
      id
      type
      ... on FanPost {
        authorIsStaff
        isReccedByStaff
        isTrusted
      }
    }
  }
`;
